import React, { useState } from 'react';
import { X, Wifi, WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { useInstanceMonitor } from '../../hooks/useInstanceMonitor';
import { toast } from '../ui/use-toast';

interface InstanceAlertProps {
  className?: string;
}

/**
 * Componente para exibir alertas de instâncias WhatsApp desconectadas
 */
const InstanceAlert: React.FC<InstanceAlertProps> = ({ className = '' }) => {
  const { disconnectedInstances, dismissAlert, reconnectInstance } = useInstanceMonitor();
  const [reconnectingInstances, setReconnectingInstances] = useState<Set<string>>(new Set());

  // Se não há instâncias desconectadas, não renderiza nada
  if (disconnectedInstances.length === 0) {
    return null;
  }

  /**
   * Manipula a tentativa de reconexão de uma instância
   */
  const handleReconnect = async (instanceId: string, instanceName: string) => {
    setReconnectingInstances(prev => new Set([...prev, instanceId]));
    
    try {
      await reconnectInstance(instanceId);
      toast({
        title: 'Reconexão iniciada',
        description: `Tentando reconectar a instância "${instanceName}". Aguarde alguns instantes.`,
        variant: 'default'
      });
    } catch (error) {

      toast({
        title: 'Erro na reconexão',
        description: `Não foi possível reconectar a instância "${instanceName}". Tente novamente.`,
        variant: 'destructive'
      });
    } finally {
      setReconnectingInstances(prev => {
        const newSet = new Set(prev);
        newSet.delete(instanceId);
        return newSet;
      });
    }
  };

  /**
   * Manipula a dispensa de um alerta
   */
  const handleDismiss = (instanceId: string, instanceName: string) => {
    dismissAlert(instanceId);
    toast({
      title: 'Alerta dispensado',
      description: `O alerta da instância "${instanceName}" foi dispensado.`,
      variant: 'default'
    });
  };

  /**
   * Formata o tempo desde a desconexão
   */
  const formatTimeSince = (disconnectedAt: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - disconnectedAt.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) {
      return 'Agora mesmo';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minuto${diffMinutes > 1 ? 's' : ''} atrás`;
    } else {
      const diffHours = Math.floor(diffMinutes / 60);
      return `${diffHours} hora${diffHours > 1 ? 's' : ''} atrás`;
    }
  };

  return (
    <div className={`fixed top-4 right-4 z-50 space-y-2 ${className}`}>
      {disconnectedInstances.map((instance) => {
        const isReconnecting = reconnectingInstances.has(instance.id);
        
        return (
          <div
            key={instance.id}
            className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 shadow-lg backdrop-blur-sm max-w-sm animate-in slide-in-from-right-5"
          >
            {/* Header do alerta */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-destructive">
                    Instância Desconectada
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatTimeSince(instance.disconnectedAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDismiss(instance.id, instance.name)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                title="Dispensar alerta"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Informações da instância */}
            <div className="mb-4">
              <div className="flex items-center space-x-2 mb-2">
                <WifiOff className="h-4 w-4 text-destructive" />
                <span className="text-sm font-medium text-foreground">
                  {instance.name}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                A instância do WhatsApp perdeu a conexão e pode não estar recebendo ou enviando mensagens.
              </p>
            </div>

            {/* Ações */}
            <div className="flex space-x-2">
              <button
                onClick={() => handleReconnect(instance.id, instance.name)}
                disabled={isReconnecting}
                className="flex-1 flex items-center justify-center space-x-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 rounded-md text-xs font-medium transition-colors"
                title="Tentar reconectar"
              >
                {isReconnecting ? (
                  <>
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    <span>Reconectando...</span>
                  </>
                ) : (
                  <>
                    <Wifi className="h-3 w-3" />
                    <span>Reconectar</span>
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleDismiss(instance.id, instance.name)}
                className="px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground border border-border hover:bg-accent rounded-md transition-colors"
                title="Dispensar por agora"
              >
                Dispensar
              </button>
            </div>

            {/* Indicador de status */}
            <div className="mt-3 pt-3 border-t border-border/50">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  Status anterior: {instance.lastStatus === 'connected' ? 'Conectado' : instance.lastStatus}
                </span>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-destructive rounded-full animate-pulse" />
                  <span className="text-destructive font-medium">Desconectado</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InstanceAlert;